import { Component, OnInit, OnDestroy } from '@angular/core';
import {
  MatLegacyDialogRef as MatDialogRef,
  MatLegacyDialog as MatDialog
} from '@angular/material/legacy-dialog';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { switchMap, filter } from 'rxjs/operators';
import { of } from 'rxjs';

import { ConfirmationDialogComponent, ConfirmationDialogData } from '@sd-portal/shared/util';
import { untilViewDestroyed } from '@sd-portal/shared/rxjs';
import { SharedPassengerService } from '../shared-passenger.service';

@Component({
  selector: 'sd-portal-add-passenger-dialog',
  templateUrl: './add-passenger-dialog.component.html',
  styleUrls: ['./add-passenger-dialog.component.scss']
})
export class AddPassengerDialogComponent implements OnInit, OnDestroy {
  userForm: FormGroup;
  invalidForm: boolean = true;
  errorMsg: string | null = null;

  constructor(
    private readonly dialog: MatDialog,
    private readonly dialogRef: MatDialogRef<AddPassengerDialogComponent>,
    private readonly fb: FormBuilder,
    private readonly passengerService: SharedPassengerService
  ) {}

  private readonly duplicateNameDialogData = Object.assign(new ConfirmationDialogData(), {
    header: 'Possible Duplicate',
    message: 'This passenger may already be saved. Do you wish to proceed?',
    confirmActionText: 'CONTINUE',
    confirmButtonColor: 'warn'
  });

  private readonly duplicateEmailDialogData = Object.assign(new ConfirmationDialogData(), {
    showCancelButton: false,
    header: 'Cannot Add Duplicate Passenger',
    message: 'A passenger with this email address already exists.',
    confirmActionText: 'OK',
    confirmButtonColor: 'warn'
  });

  ngOnInit() {
    this.userForm = this.fb.group({
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      emailAddress: ['', Validators.email]
    });
  }
  ngOnDestroy() {}

  cancel() {
    this.dialogRef.close(null);
  }

  submit() {
    if (this.userForm) {
      const { firstName, lastName, emailAddress } = this.userForm.value;

      const emailDupe$ =
        emailAddress != null && emailAddress.length > 0
          ? this.passengerService.isDuplicatePassengerEmail(emailAddress)
          : of(false);

      const nameDupe$ = this.passengerService.isDuplicatePassengerName(firstName, lastName).pipe(
        switchMap(isDuplicate => (isDuplicate ? this.duplicateNameDialog$() : of(true))),
        filter(confirmed => confirmed)
      );

      emailDupe$
        .pipe(
          untilViewDestroyed(this),
          switchMap(isDuplicate => (isDuplicate ? this.duplicateEmailDialog$() : of(true))),
          filter(confirmed => confirmed),
          switchMap(_ => nameDupe$)
        )
        .subscribe(_ => {
          this.dialogRef.close(this.userForm.value);
        });
    }
  }

  private readonly duplicateNameDialog$ = () =>
    this.duplicatePersonDialog$(this.duplicateNameDialogData);

  private readonly duplicateEmailDialog$ = () =>
    this.duplicatePersonDialog$(this.duplicateEmailDialogData);

  private readonly duplicatePersonDialog$ = (data: ConfirmationDialogData) =>
    this.dialog
      .open(ConfirmationDialogComponent, {
        data,
        disableClose: true
      })
      .afterClosed();
}
